'use client';

/**
 * Inline form panel that sits above the CodeMirror editor for
 * function-content rows and lets the owner edit common manifest fields
 * (runtime, entrypoint, fetch allowlist, secrets, limits) without
 * hand-editing YAML.
 *
 * ## What this panel intentionally does NOT edit
 *
 * The manifest describes the function as a *capability* — code that
 * runs in a sandboxed runtime, with an entrypoint, an outbound-fetch
 * allowlist, a list of secret bindings it expects, and per-invocation
 * resource caps. None of those depend on how the function is exposed.
 *
 * How the function is exposed (HTTP path/method/auth, cron expression,
 * LLM tool name + arg schema) is a *binding* on a specific agent —
 * stored under `agent_configs.skills.{custom_http,cron,custom_tools}`.
 * Bindings are added through the agent's Functions panel ("Connect"
 * button → Factory Agent prompt) or the `add_to_skill` factory tool.
 *
 * Conflating them in this form (as we did briefly) leaks per-binding
 * fields into the function's frontmatter, where they have no effect at
 * runtime (the SDK reads them from the binding row, never the manifest)
 * and bake an HTTP-shape into otherwise-portable code. Keeping the cut
 * sharp here is what allows one function to power multiple bindings.
 *
 * ## Two-way binding contract
 *
 *   - On mount and whenever the parent passes a new `source`, the panel
 *     re-parses the `/* @robutler-function ... *\/` block and seeds its
 *     form state. If no block is present, the panel collapses into a
 *     single "Insert manifest" button.
 *   - On every form edit, we rebuild the YAML body, splice it back into
 *     the comment block, and call `onPatchSource` with the updated full
 *     source. The parent (the editor page) then forwards the change to
 *     CodeMirror via a single `dispatch({ changes })` so undo history
 *     stays intact.
 *
 * The panel never overrides fields the user already had; unknown keys
 * round-trip via the YAML body even though they have no UI control.
 */

'use client';

import { useEffect, useMemo, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { ChevronDown, ChevronRight, Plus, Sparkles } from 'lucide-react';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { cn } from '@/lib/utils';

const FRONTMATTER_RE = /(\/\*\*?[\s*]*@robutler-function\b)([\s\S]*?)(\*\/)/m;

type Manifest = Record<string, unknown> & {
  runtime?: string;
  entrypoint?: string;
  description?: string;
  permissions?: { fetch?: string[]; secrets?: string[]; kv?: string };
  limits?: { wallMs?: number; cpuMs?: number; memoryMb?: number };
};

const DEFAULT_TEMPLATE = `/**
 * @robutler-function
 * runtime: js-v1
 * entrypoint: handler
 */
`;

function parseManifest(source: string): Manifest | null {
  const m = FRONTMATTER_RE.exec(source);
  if (!m) return null;
  const inner = m[2]
    .split('\n')
    .map((l) => l.replace(/^\s*\*\s?/, ''))
    .join('\n')
    .trim();
  if (inner.length === 0) return {};
  if (inner.startsWith('{')) {
    try { return JSON.parse(inner) as Manifest; } catch { return {}; }
  }
  // Bail on legacy `@key value` format — show empty form rather than
  // mangling fields. Owners can convert manually.
  if (/^@\w+/m.test(inner.split('\n')[0] ?? '')) return {};
  try {
    const parsed = parseYaml(inner);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Manifest;
  } catch {
    return {};
  }
}

function rebuildSource(source: string, manifest: Manifest): string {
  const m = FRONTMATTER_RE.exec(source);
  const yaml = stringifyYaml(manifest, { lineWidth: 0 }).trimEnd();
  const indented = yaml.split('\n').map((line) => ` * ${line}`).join('\n');
  const newBody = `\n${indented}\n `;

  if (!m) {
    const block = `/**\n * @robutler-function${newBody}*/\n\n`;
    return block + source.replace(/^\n+/, '');
  }
  return source.slice(0, m.index) + m[1] + newBody + m[3] + source.slice(m.index + m[0].length);
}

// `python-pyodide-v1` is deferred (ADR-0008); only `js-v1` is selectable
// in the UI. The validator surfaces a clear error if a saved manifest
// pins the deferred runtime.
const RUNTIMES = ['js-v1'] as const;

export function ManifestHelper({
  source,
  onPatchSource,
}: {
  source: string;
  onPatchSource: (next: string) => void;
}) {
  const t = useT();
  const initial = useMemo(() => parseManifest(source), [source]);
  const [open, setOpen] = useState(true);
  const [m, setM] = useState<Manifest>(() => initial ?? {});

  // Re-seed the form whenever the source changes externally (e.g.
  // restore from history, AI edit, or initial load).
  useEffect(() => {
    if (initial) setM(initial);
  }, [initial]);

  // No frontmatter: render only the insert-template button.
  if (!initial) {
    return (
      <div className="shrink-0 border-b border-border bg-card px-3 py-2 flex items-center gap-2 text-xs">
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">{t('manifest.noneDetected')}</span>
        <button
          onClick={() => onPatchSource(DEFAULT_TEMPLATE + source.replace(/^\s*/, ''))}
          className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-border hover:bg-accent transition-colors"
        >
          <Plus className="h-3 w-3" />
          {t('manifest.insert')}
        </button>
      </div>
    );
  }

  const patch = (next: Manifest) => {
    setM(next);
    onPatchSource(rebuildSource(source, next));
  };

  return (
    // Opaque card background so the form reads as a solid sub-panel
    // inside the editor — both on the standalone /content page (sits
    // on the page bg) and inside the workspace widget where the body
    // is `glass-strong` (88% bg + blur). Using `bg-muted/20` here
    // stacked the two translucent layers and made the form look like
    // an empty rectangle floating over the canvas grid.
    <div className="shrink-0 border-b border-border bg-card">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/60 transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span className="font-medium">Manifest</span>
        <span className="text-muted-foreground">
          {m.runtime ?? 'js-v1'} · {m.entrypoint ?? t('manifest.handlerPlaceholder')}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
          <Field label={t('manifest.runtime')}>
            <select
              value={(m.runtime as string) ?? 'js-v1'}
              onChange={(e) => patch({ ...m, runtime: e.target.value })}
              className="w-full border border-border rounded-md bg-background px-2 py-1 text-[11px]"
            >
              {RUNTIMES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label={t('manifest.entrypoint')}>
            <input
              value={(m.entrypoint as string) ?? ''}
              onChange={(e) => patch({ ...m, entrypoint: e.target.value })}
              placeholder={t('manifest.handlerPlaceholder')}
              className="w-full border border-border rounded-md bg-background px-2 py-1 text-[11px] font-mono"
            />
          </Field>
          <Field label="permissions.fetch (one per line)" className="col-span-2">
            <textarea
              value={(m.permissions?.fetch ?? []).join('\n')}
              onChange={(e) => {
                const list = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                patch({
                  ...m,
                  permissions: { ...(m.permissions ?? {}), fetch: list.length ? list : undefined },
                });
              }}
              placeholder={'https://api.openai.com/*\nhttps://api.example.com/*'}
              rows={2}
              className="w-full border border-border rounded-md bg-background px-2 py-1 text-[11px] font-mono"
            />
          </Field>
          <Field label="permissions.secrets (comma-separated)">
            <input
              value={(m.permissions?.secrets ?? []).join(', ')}
              onChange={(e) => {
                const list = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                patch({
                  ...m,
                  permissions: { ...(m.permissions ?? {}), secrets: list.length ? list : undefined },
                });
              }}
              placeholder="OPENAI_KEY, STRIPE_KEY"
              className="w-full border border-border rounded-md bg-background px-2 py-1 text-[11px] font-mono"
            />
          </Field>
          <Field label="limits.wallMs">
            <input
              type="number"
              min={1}
              value={m.limits?.wallMs ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? undefined : Number(e.target.value);
                patch({ ...m, limits: { ...(m.limits ?? {}), wallMs: Number.isFinite(v) ? v : undefined } });
              }}
              placeholder="5000"
              className="w-full border border-border rounded-md bg-background px-2 py-1 text-[11px] font-mono"
            />
          </Field>
          <p className="col-span-2 text-[10px] text-muted-foreground leading-relaxed">
            HTTP path / method / auth, cron schedules, and tool argument
            schemas live on the agent that exposes this function — open
            the agent&apos;s Functions panel and use Connect to add a
            binding.
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={cn('flex flex-col gap-0.5', className)}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
